import { AIAgentMessage } from '../../../models/AIAgent'
import React, { useState } from 'react'
import classNames from 'classnames'
import { Collapse } from 'react-bootstrap'
import { ReactComponent as ZocksLogo } from '../../../assets/ZocksLogo.svg'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { AlwaysScrollToBottom } from '../../../components/AlwaysScrollToBottom/AlwaysScrollToBottom'
import '../AIAgentChat.scss'
import { AgentChatInput } from './AgentChatInput'
import { AgentIntro } from './AgentIntro'

interface AgentChatProps {
  messages: AIAgentMessage[]
  handleSubmit: (chatMessage: string) => Promise<void>
  isLoading: boolean
  disabled?: boolean
  infoText?: string
  header?: React.ReactNode
  collapsible?: boolean
  collapseByDefault?: boolean
  className?: string
}

export const AIAgentChat: React.FC<AgentChatProps> = ({
  messages,
  handleSubmit,
  isLoading,
  disabled,
  infoText,
  header,
  collapsible = false,
  collapseByDefault = false,
  className = '',
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(collapsible ? collapseByDefault : false)

  const toggleChat = () => {
    if (collapsible) {
      setIsCollapsed((prev) => !prev)
    }
  }

  const getIntroText = () => {
    if (messages.length > 1) {
      return null
    }

    if (messages.length === 1) {
      return messages[0].type === 'agent' ? messages[0].message : null
    }

    return 'I’m your assistant. I’ll help you get answers from data you don’t have the time or energy to go through.'
  }

  const introText = getIntroText()

  return (
    <div className={`d-flex flex-column border zocks-border-0.5 ${className}`}>
      <div
        className={classNames('d-flex py-4 px-4 align-items-center gap-3', { 'border-bottom zocks-border-color-gray-200': !isCollapsed })}
        onClick={toggleChat}
      >
        <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-header-logo zocks-border-0.25'>
          <ZocksLogo width={18} height={18} />
        </div>
        <div className='d-flex flex-column'>
          <span className='paragraph-small bold'>ZOCKS</span>
          <span className='paragraph-small'>AI Assistant</span>
        </div>
        {infoText ? (
          <div className='ms-auto'>
            <div className='d-flex ai-agent-chat-header-info-icon align-items-center justify-content-center p-2 zocks-border-0.25'>
              <Tooltip className='ai-agent-chat-header-info-tooltip paragraph-small' title={infoText} placement={'top'} show={true}>
                <i className='bi bi-info-lg' />
              </Tooltip>
            </div>
          </div>
        ) : null}
        {header ? header : null}
        {collapsible ? (
          <i
            className={classNames('bi ms-auto', {
              'bi-chevron-expand': isCollapsed,
              'bi-chevron-contract': !isCollapsed,
            })}
          />
        ) : null}
      </div>
      <Collapse in={!isCollapsed} dimension={'height'} className='flex-grow-1'>
        <div>
          <div className='d-flex flex-column justify-content-between h-100 p-3'>
            {introText ? <AgentIntro introText={introText} /> : <AgentChatMessages messages={messages} isLoading={isLoading} />}
            <AgentChatInput handleSubmit={handleSubmit} isLoading={isLoading} disabled={disabled} />
          </div>
        </div>
      </Collapse>
    </div>
  )
}

interface AgentChatMessagesProps {
  messages: AIAgentMessage[]
  isLoading: boolean
}

const _DEFAULT_LOADING_MESSAGE = 'Thinking'
const _LONG_LOADING_MESSAGE = 'Complex operations might take a bit longer, please hold on'

const AgentChatMessages: React.FC<AgentChatMessagesProps> = ({ messages, isLoading }) => {
  const [loadingMessage, setLoadingMessage] = useState(_DEFAULT_LOADING_MESSAGE)
  const [prevIsLoading, setPrevIsLoading] = useState(isLoading)

  if (prevIsLoading !== isLoading) {
    if (isLoading) {
      setTimeout(() => {
        setLoadingMessage(_LONG_LOADING_MESSAGE)
      }, 15000)
    } else {
      setLoadingMessage(_DEFAULT_LOADING_MESSAGE)
    }
    setPrevIsLoading(isLoading)
  }

  return (
    <div className='d-flex flex-column zocks-gap-1.25 ai-agent-chat-messages flex-grow-1'>
      {messages.map(({ type, message }, idx) => {
        if (idx === 0 && type === 'agent') {
          return null
        }
        if (type === 'user') {
          return (
            <div key={idx} className='d-flex px-2 justify-content-end'>
              <div className='zocks-p-0.75 ai-agent-chat-message zocks-border-0.5 zocks-bg-color-primary-light'>{message}</div>
            </div>
          )
        }
        return (
          <div key={idx} className='d-flex zocks-gap-0.75 px-2'>
            <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25'>
              <ZocksLogo width={12} height={12} />
            </div>
            <div className='ai-agent-chat-message paragraph-medium'>{message}</div>
          </div>
        )
      })}
      {isLoading ? (
        <div className='d-flex zocks-gap-0.75 px-2'>
          <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25'>
            <ZocksLogo width={12} height={12} />
          </div>
          <span className='paragraph-medium zocks-text-gray-500 m-0'>
            {loadingMessage}
            <div className='d-inline-block ai-agent-chat-loader ms-2' />
          </span>
        </div>
      ) : null}
      <AlwaysScrollToBottom dependencies={[messages]} />
    </div>
  )
}
